import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LazyImage from './LazyImage';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicLandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleGetStarted = () => {
    navigate('/auth/register');
  };

  const handleViewMembership = () => {
    navigate('/membership');
  };

  const handleJoinNewsletter = async () => {
    if (!email) {
      setNewsletterMessage('Please enter your email address');
      setTimeout(() => setNewsletterMessage(''), 3000);
      return;
    }

    setIsSubscribing(true);
    setNewsletterMessage('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email })
      });

      const result = await response.json();

      if (response.ok) {
        setNewsletterMessage('🎉 Successfully subscribed! Welcome to the community.');
        setEmail('');
      } else {
        setNewsletterMessage(result.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubscribing(false);
      setTimeout(() => setNewsletterMessage(''), 5000);
    }
  };

  return (
    <div className="landing-page public-landing">
      {/* Header */}
      <div style={{
        background: 'var(--card)',
        padding: '1rem 2rem',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src="/logo.webp" 
            alt="Zero Give Up" 
            style={{ height: '40px', marginRight: '1rem' }}
          />
          <h1 style={{ fontSize: '1.5rem', color: 'var(--primary)', margin: 0 }}>ZeroGiveUp</h1>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button 
            onClick={() => navigate('/auth/login')}
            style={{
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Sign In
          </button>
          <button 
            onClick={handleGetStarted}
            style={{
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Start with Starter Access
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Rediscover Focus, Energy, and Confidence — One Small Win at a Time.
            </h1>
            <p className="hero-subtitle">
              Join ZeroGiveUp, the 90-day challenge that helps you rebuild your rhythm, habits, and purpose — surrounded by people who won't let you give up.
            </p>
            <div className="hero-cta">
              <button 
                className="btn-primary btn-large hero-btn"
                onClick={handleGetStarted}
              >
                Start with Starter Access
              </button>
              <div className="hero-info">
                Join the community and start your transformation journey.
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-card">
              <div className="progress-demo">
                <h3>📈 Your 90-Day Journey</h3>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '75%' }}></div>
                </div>
                <p>Day 67 of 90 • 23 days to go!</p>
                <div className="achievement-badges">
                  <span className="badge">🔥 67-day streak</span>
                  <span className="badge">💪 Habit Master</span>
                  <span className="badge">🎯 Goal Crusher</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Placeholder for moved section */}

      {/* Is This For You Section */}
      <section className="for-you-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Is this for you?</h2>
          </div>
          <div className="for-you-grid">
            <div className="for-you-item">
              <div className="for-you-icon">💭</div>
              <p>You're tired of starting over and want to finally feel in control again.</p>
            </div>
            <div className="for-you-item">
              <div className="for-you-icon">🏆</div>
              <p>You crave progress — small wins that actually stick.</p>
            </div>
            <div className="for-you-item">
              <div className="for-you-icon">🤝</div>
              <p>You want accountability and community that feels real.</p>
            </div>
            <div className="for-you-item">
              <div className="for-you-icon">🎯</div>
              <p>You're ready to follow through this time — for yourself.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Founding Member Promotion */}
      <section className="founding-member-promo">
        <div className="container">
          <div className="founding-member-banner" style={{
            background: 'linear-gradient(135deg, #FFD700, #FFA500)',
            border: '3px solid #FFD700',
            borderRadius: '16px',
            padding: '2.5rem',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            margin: '2rem auto',
            maxWidth: '800px'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'repeating-linear-gradient(45deg, transparent, transparent 15px, rgba(255,255,255,0.1) 15px, rgba(255,255,255,0.1) 30px)',
              pointerEvents: 'none'
            }}></div>
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👑</div>
              
              <h2 style={{ 
                color: '#8B4513', 
                marginBottom: '1rem', 
                fontSize: '2rem', 
                fontWeight: 'bold'
              }}>
                Starter Access Includes
              </h2>
              
              <ul style={{ 
                color: '#8B4513', 
                fontSize: '1.1rem', 
                marginBottom: '1.5rem',
                textAlign: 'left',
                listStyle: 'none',
                padding: 0,
                maxWidth: '300px',
                margin: '0 auto 1.5rem auto'
              }}>
                <li style={{ marginBottom: '0.5rem' }}>🎯 21-Day Reset Program</li>
                <li style={{ marginBottom: '0.5rem' }}>📊 Habit & Goal Trackers</li>
                <li style={{ marginBottom: '0.5rem' }}>🚀 First 8 Days of Evolve 90</li>
                <li style={{ marginBottom: '0.5rem' }}>📚 Bonus Books</li>
              </ul>
              
              <p style={{ 
                color: '#8B4513', 
                fontSize: '1.2rem', 
                marginBottom: '1.5rem',
                fontWeight: '600'
              }}>
                $47 one-time payment. Keep your progress forever.
              </p>
              
              <button
                onClick={handleViewMembership}
                style={{
                  background: 'linear-gradient(135deg, #8B4513, #A0522D)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(139, 69, 19, 0.3)'
                }}
              >
                Claim My Starter Access
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="how-it-works" className="what-you-get-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What you get when you join</h2>
          </div>
          
          <div className="feature-grid">
            <div className="feature-card highlighted">
              <div className="feature-icon">🔥</div>
              <h3>Evolve 90 Program</h3>
              <p>Daily 5-minute lessons to reset your mindset and rebuild consistency.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">📈</div>
              <h3>Progress Tracker</h3>
              <p>See your growth every day with streaks, badges, and reminders that keep you moving.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">💬</div>
              <h3>Community Support</h3>
              <p>Real people cheering you on — because you don't have to do it alone anymore.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🎓</div>
              <h3>Courses</h3>
              <p>Short, practical lessons on mindset, motivation, and habits that help you stay consistent.</p>
            </div>
          </div>
          
          <div className="section-cta">
            <button 
              className="btn-primary btn-large"
              onClick={handleGetStarted}
            >
              Start with Starter Access
            </button>
          </div>
        </div>
      </section>

      {/* Platform Screenshots Section */}
      <section className="platform-screenshots-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">See the platform in action</h2>
            <p className="section-subtitle">
              Get a glimpse inside the ZeroGiveUp community and see how our tools work together to support your growth.
            </p>
          </div>
          
          <div className="screenshots-grid">
            <div className="screenshot-item">
              <div className="screenshot-image">
                <LazyImage 
                  src="https://customer-assets.emergentagent.com/job_4bde5771-7e83-49e5-b35f-d61910948888/artifacts/1bav15o8_Screenshot%202025-10-18%20170641.png" 
                  alt="Community Feed - Share your progress and connect with others"
                  loading="lazy"
                  placeholder={true}
                />
              </div>
              <div className="screenshot-info">
                <h4>🌟 Community Feed</h4>
                <p>Share your daily wins, challenges, and milestones with a supportive community that celebrates every step forward.</p>
              </div>
            </div>
            
            <div className="screenshot-item">
              <div className="screenshot-image">
                <LazyImage 
                  src="https://customer-assets.emergentagent.com/job_4bde5771-7e83-49e5-b35f-d61910948888/artifacts/aheuyrvx_Screenshot%202025-10-18%20170659.png" 
                  alt="Community Groups - Connect with like-minded individuals"
                  loading="lazy"
                  placeholder={true}
                />
              </div>
              <div className="screenshot-info">
                <h4>👥 Community Groups</h4>
                <p>Join focused groups that match your goals and connect with people walking the same 90-day path.</p>
              </div>
            </div>
            
            <div className="screenshot-item">
              <div className="screenshot-image">
                <LazyImage 
                  src="https://customer-assets.emergentagent.com/job_4bde5771-7e83-49e5-b35f-d61910948888/artifacts/2xh6m7pt_Screenshot%202025-10-18%20170716.png" 
                  alt="Forum Categories - Engage in meaningful discussions"
                  loading="lazy"
                  placeholder={true}
                />
              </div>
              <div className="screenshot-info">
                <h4>💬 Forum Discussions</h4>
                <p>Ask questions, share insights, and stay accountable — progress feels easier when you're not doing it alone.</p>
              </div>
            </div>
            
            <div className="screenshot-item">
              <div className="screenshot-image">
                <LazyImage 
                  src="https://customer-assets.emergentagent.com/job_4bde5771-7e83-49e5-b35f-d61910948888/artifacts/gqujm8eg_Screenshot%202025-10-18%20170736.png" 
                  alt="Evolve 90 Dashboard - Track your daily progress"
                  loading="lazy"
                  placeholder={true}
                />
              </div>
              <div className="screenshot-info">
                <h4>📈 Evolve 90 Dashboard</h4>
                <p>Track your 90-day journey with visual progress, streak counters, and daily lessons that build unstoppable momentum.</p>
              </div>
            </div>
            
            <div className="screenshot-item">
              <div className="screenshot-image">
                <img 
                  src="https://customer-assets.emergentagent.com/job_4bde5771-7e83-49e5-b35f-d61910948888/artifacts/71d3rsyf_Screenshot%202025-10-18%20170752.png" 
                  alt="Daily Lessons - Bite-sized content for real growth"
                  loading="lazy"
                />
              </div>
              <div className="screenshot-info">
                <h4>📚 Daily Lessons</h4>
                <p>Access bite-sized, practical lessons designed to create real change. Each lesson takes just minutes to read and apply.</p>
              </div>
            </div>
          </div>
          
          <div className="section-cta">
            <button 
              className="btn-primary btn-large"
              onClick={handleGetStarted}
            >
              Start with Starter Access
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Your Comeback?</h2>
            <p>Join ZeroGiveUp and start your transformation journey today.</p>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-light)', margin: '1rem 0' }}>
              🔸 No pressure. Just progress.
            </p>
            <div className="cta-buttons">
              <button 
                className="btn-primary btn-large"
                onClick={handleGetStarted}
              >
                Start with Starter Access
              </button>
            </div>
          </div>
        </div>
      </section>
      <Footer setCurrentPage={() => {}} />
    </div>
  );
};

export default PublicLandingPage;